import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MotionConfig, motion, useMotionValueEvent, useScroll, useTransform } from 'motion/react';

// import '@fontsource/geist-sans/400.css';
// import '@fontsource/geist-sans/500.css';
// import '@fontsource/geist-sans/600.css';
// import '@fontsource/geist-sans/700.css';
import '@fontsource-variable/geist';

import styles from './NewLandingPage.module.css';
import HowItWorksSection from '@/components/molecules/branding/HowItWorksSection';
import LandingButton from '@/components/molecules/branding/LandingButton';
import LandingLanguageSelector from '@/components/molecules/branding/LandingLanguageSelector';
import LandingMobileMenu from '@/components/molecules/branding/LandingMobileMenu';
import LandingNavbar from '@/components/molecules/branding/LandingNavbar';
import { useLandingSectionSpy } from '@/components/molecules/branding/useLandingSectionSpy';
import {
  ALL_LANDING_NAV_ITEMS,
  LANDING_NAVBAR_ON_COLOR_SECTION_IDS
} from '@/components/molecules/branding/landingNavItems';
import HeroGradientDecor from '@/assets/images/branding/hero-gradient-decor.png';
import Logo from '@/assets/images/branding/logo.svg';
import LogoIcon from '@/assets/images/branding/logo-icon.svg';
import HeroImage1 from '@/assets/images/branding/hero-img-1.png';
import HeroImage2 from '@/assets/images/branding/hero-img-2.png';
import HeroMockup from '@/assets/images/branding/hero-mockup.png';
import HeroImage3 from '@/assets/images/branding/hero-img-3.png';
import HeroImage4 from '@/assets/images/branding/hero-img-4.png';
import ProblemImage1 from '@/assets/images/branding/problem-1.svg';
import ProblemImage2 from '@/assets/images/branding/problem-2.svg';
import ProblemImage3 from '@/assets/images/branding/problem-3.svg';
import ProblemImage4 from '@/assets/images/branding/problem-4.svg';
import ProblemImage5 from '@/assets/images/branding/problem-5.svg';
import CrossIcon from '@/assets/images/branding/cross.svg';
import FlowImage1 from '@/assets/images/branding/flow-1.svg';
import FlowImage2 from '@/assets/images/branding/flow-2.svg';
import FlowImage3 from '@/assets/images/branding/flow-3.svg';
import FlowImage4 from '@/assets/images/branding/flow-4.svg';
import FlowImage5 from '@/assets/images/branding/flow-5.svg';
import SolutionImage from '@/assets/images/branding/solution.svg';
import CheckIcon from '@/assets/images/branding/check.svg';
import FeatureImage1 from '@/assets/images/branding/features-1.svg';
import FeatureImage2 from '@/assets/images/branding/features-2.svg';
import FeatureImage3 from '@/assets/images/branding/features-3.svg';
import FeatureImage4 from '@/assets/images/branding/features-4.svg';
import FeatureImage5 from '@/assets/images/branding/features-5.svg';
import EcosystemBudgetOwnerImage from '@/assets/images/branding/ecosystem-budget-owner.svg';
import EcosystemBudgetValidatorImage from '@/assets/images/branding/ecosystem-budget-validator.svg';
import EcosystemBudgetMemberImage from '@/assets/images/branding/ecosystem-budget-member.svg';
import FooterMockupImage from '@/assets/images/branding/footer-mockup.svg';
import PlusIcon from '@/assets/images/branding/plus.svg';
import MinusIcon from '@/assets/images/branding/minus.svg';
import { PAGE } from '@/routes';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowRightIcon, ChevronRightIcon } from 'lucide-react';
const PROBLEM_IMAGES = [ProblemImage1, ProblemImage2, ProblemImage3, ProblemImage4, ProblemImage5];

const PROBLEM_LIST_ITEMS = [
  'No shared Accounting Center',
  'No real-time view of plan vs. actual',
  'Disorganized invoicing leads to a budget deficit',
  "The cost of your team's time is invisible in your budget."
];

const FEATURE_CARDS = [
  {
    title: 'Budgets Built Around Your Structure',
    description:
      'Set your revenue targets and expense caps once, and let every project, contract, or department draw from the same plan.',
    image: FeatureImage1,
    colSpan: 'md:col-span-2'
  },
  {
    title: 'Four Dimensions. One Budget',
    description:
      'Organize your budget around your projects, contracts, departments, products, or services not forced into a fixed hierarchy.',
    image: FeatureImage2,
    colSpan: 'md:col-span-2'
  },
  {
    title: 'Every Invoice. Every Angle',
    description:
      'Capture an invoice by photo or file, then simply describing it out loud, and BatJet allocates it to the right Accounting Center automatically.',
    image: FeatureImage3,
    colSpan: 'md:col-span-2'
  },
  {
    title: 'Hours Become Cost Automatically',
    description:
      'Team members log their hours, and BatJet converts that time into real cost. It counted in the budget, not left out of it.',
    image: FeatureImage4,
    colSpan: 'md:col-span-3'
  },
  {
    title: 'Nothing Enters the Budget Unconfirmed.',
    description:
      'Every invoice and logged hour gets reviewed by a Validator before it counts, so your numbers stay accurate and accountable.',
    image: FeatureImage5,
    colSpan: 'md:col-span-3'
  }
];

const INSIGHT_DESCRIPTION =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.';

const INSIGHT_CARDS = [
  { title: 'Parallel Allocation', description: INSIGHT_DESCRIPTION },
  { title: 'Live Budget', description: INSIGHT_DESCRIPTION },
  { title: 'The Integration Gap', description: INSIGHT_DESCRIPTION },
  { title: 'Cash Flow', description: INSIGHT_DESCRIPTION }
];

const FAQ_ANSWER =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.';

const FAQ_ITEMS = [
  { question: "Is my organization's financial data secure?", answer: FAQ_ANSWER },
  { question: 'Do I or my team need an accounting background to use it?', answer: FAQ_ANSWER },
  { question: 'Is BatJet a replacement for my accounting software?', answer: FAQ_ANSWER },
  { question: 'What is the difference between an employee and a freelancer in BatJet?', answer: FAQ_ANSWER }
];

const INSIGHT_PER_VIEW = {
  mobile: 1,
  tablet: 3,
  desktop: 4
};
const INSIGHT_DESKTOP_MIN_WIDTH = 1280;
const INSIGHT_CARD_GAP = 24;

const InsightCarousel = () => {
  const [perView, setPerView] = useState(INSIGHT_PER_VIEW.desktop);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const desktop = window.matchMedia(`(min-width: ${INSIGHT_DESKTOP_MIN_WIDTH}px)`);
    const tablet = window.matchMedia('(min-width: 768px)');
    const update = () => {
      if (desktop.matches) setPerView(INSIGHT_PER_VIEW.desktop);
      else if (tablet.matches) setPerView(INSIGHT_PER_VIEW.tablet);
      else setPerView(INSIGHT_PER_VIEW.mobile);
    };
    update();
    desktop.addEventListener('change', update);
    tablet.addEventListener('change', update);
    return () => {
      desktop.removeEventListener('change', update);
      tablet.removeEventListener('change', update);
    };
  }, []);

  const maxIndex = Math.max(0, INSIGHT_CARDS.length - perView);

  useEffect(() => {
    setIndex((current) => Math.min(current, maxIndex));
  }, [maxIndex]);

  const canPrev = index > 0;
  const canNext = index < maxIndex;

  if (INSIGHT_CARDS.length === 0) {
    return (
      <p className="py-16 text-center text-base leading-[24px] tracking-[-0.32px] text-[#383838]">
        Insight are not available for now. we will update it later please stay tune!
      </p>
    );
  }

  return (
    <div>
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{
            gap: INSIGHT_CARD_GAP,
            transform: `translateX(calc(${-index} * (100% + ${INSIGHT_CARD_GAP}px) / ${perView}))`
          }}
        >
          {INSIGHT_CARDS.map((card, cardIndex) => (
            <div
              key={card.title + cardIndex}
              className="flex shrink-0 items-end gap-2"
              style={{ flexBasis: `calc((100% - ${(perView - 1) * INSIGHT_CARD_GAP}px) / ${perView})` }}
            >
              <div aria-hidden className="relative shrink-0 w-[48px] h-[64px] select-none md:w-[64px] md:h-[80px]">
                <span className="absolute inset-0 text-right font-roboto-condensed font-black text-[72px] leading-[64px] tracking-[-4px] uppercase text-[#5F46DE] [-webkit-text-stroke:4px_#5F46DE] md:text-[96px] md:leading-[80px] md:tracking-[-6.4px]">
                  {cardIndex + 1}
                </span>
                <span className="absolute inset-0 text-right font-roboto-condensed font-black text-[72px] leading-[64px] tracking-[-4px] uppercase text-background md:text-[96px] md:leading-[80px] md:tracking-[-6.4px]">
                  {cardIndex + 1}
                </span>
              </div>

              <div className="flex-1 min-w-0 rounded-[8px] flex flex-col justify-between gap-6 h-[236px] p-4 bg-[linear-gradient(180deg,#FFFFFF_0%,#F4F4F4_69.71%,#FAFAFA_100%)]">
                <div className="flex-1 min-h-0 overflow-hidden">
                  <h4 className="mb-2 font-medium text-[20px] text-[#0A0A0A] leading-[30px] tracking-[-0.4px]">
                    {card.title}
                  </h4>
                  <p className="text-[#383838] text-sm leading-[22px] tracking-[-0.42px] line-clamp-5">
                    {card.description}
                  </p>
                </div>
                <button className="inline-flex gap-1 w-full items-center justify-end text-primary font-semibold text-base cursor-pointer hover:underline tracking-[-0.32px]">
                  View More <ArrowRightIcon className="size-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-8 flex items-center justify-center gap-4 md:mt-[56px]">
        <span className={canPrev ? undefined : 'cursor-not-allowed'}>
          <LandingButton
            variant="secondary"
            aria-label="Previous insights"
            disabled={!canPrev}
            onClick={() => setIndex((current) => Math.max(0, current - 1))}
            className="disabled:opacity-40 disabled:pointer-events-none"
          >
            {'<'}
          </LandingButton>
        </span>
        <span className={canNext ? undefined : 'cursor-not-allowed'}>
          <LandingButton
            variant="secondary"
            aria-label="Next insights"
            disabled={!canNext}
            onClick={() => setIndex((current) => Math.min(maxIndex, current + 1))}
            className="disabled:opacity-40 disabled:pointer-events-none"
          >
            {'>'}
          </LandingButton>
        </span>
      </div>
    </div>
  );
};

const HERO_EASE = [0.22, 1, 0.36, 1] as const;
const XL_MIN_WIDTH = 1280;

const useMinWidth = (minWidth: number) => {
  const query = `(min-width: ${minWidth}px)`;
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);

  return matches;
};

const LandingReveal = ({
  enabled,
  className,
  children
}: {
  enabled: boolean;
  className?: string;
  children: React.ReactNode;
}) => {
  if (!enabled) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.6, ease: HERO_EASE }}
    >
      {children}
    </motion.div>
  );
};

const FLOW_TEXT = 'A budget you can’t track in real time isn’t a budget. It’s a guess.';

const FLOW_FLOAT_IMAGES = [
  {
    src: FlowImage1,
    className:
      'absolute left-1 top-6 w-12 md:-left-[56px] md:-top-[56px] md:w-16 lg:-left-[150px] lg:-top-[80px] lg:w-auto',
    fromY: 140
  },
  {
    src: FlowImage2,
    className:
      'absolute right-2 -top-4 w-12 md:left-[64%] md:right-auto md:-top-[88px] md:w-16 lg:left-[62%] lg:-top-[160px] lg:w-auto',
    fromY: 52
  },
  {
    src: FlowImage3,
    className:
      'absolute right-0 top-[46%] w-12 md:-right-[62px] md:top-0 md:w-16 lg:-right-[200px] lg:top-[88px] lg:w-auto',
    fromY: 120
  },
  {
    src: FlowImage4,
    className:
      'absolute right-4 -bottom-2 w-10 md:right-10 md:-bottom-[104px] md:w-14 lg:right-2 lg:-bottom-[120px] lg:w-auto',
    fromY: 160
  },
  {
    src: FlowImage5,
    className:
      'absolute left-2 -bottom-1 w-12 md:-left-[48px] md:-bottom-[72px] md:w-16 lg:-left-[130px] lg:-bottom-[190px] lg:w-auto',
    fromY: 200
  }
];

const FlowFloatImage = ({
  image,
  progress,
  animated
}: {
  image: (typeof FLOW_FLOAT_IMAGES)[number];
  progress: ReturnType<typeof useScroll>['scrollYProgress'];
  animated: boolean;
}) => {
  const y = useTransform(progress, [0, 0.9], [animated ? image.fromY : 0, 0], { clamp: true });

  return <motion.img src={image.src} alt="" className={image.className} style={animated ? { y } : undefined} />;
};

const FlowSection = ({ animated }: { animated: boolean }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const animatedRef = useRef(animated);
  const [filledCount, setFilledCount] = useState(0);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end']
  });

  animatedRef.current = animated;

  useMotionValueEvent(scrollYProgress, 'change', (value) => {
    if (!animatedRef.current) return;

    const progress = Math.min(1, value / 0.9);
    const next = Math.min(FLOW_TEXT.length, Math.ceil(progress * FLOW_TEXT.length));
    setFilledCount((current) => (current === next ? current : next));
  });

  return (
    <section
      ref={sectionRef}
      id="flow"
      className={animated ? 'relative h-[165vh]' : 'relative flex items-center py-16 md:min-h-dvh md:py-0'}
      aria-label="Flow"
    >
      <div
        className={
          animated ? 'sticky top-0 flex h-dvh items-center justify-center' : 'flex w-full items-center justify-center'
        }
      >
        <div className="relative w-full max-w-[650px] px-14 py-16 md:px-0 md:py-0">
          {FLOW_FLOAT_IMAGES.map((image) => (
            <FlowFloatImage key={image.src} image={image} progress={scrollYProgress} animated={animated} />
          ))}
          <p
            className={`font-medium text-[28px] leading-[36px] tracking-[-0.8px] text-center md:text-[40px] md:leading-[50px] md:tracking-[-1.2px] lg:text-[56px] lg:leading-[67px] lg:tracking-[-1.68px] ${
              animated ? 'text-[#A6A6A6]' : 'text-[#0A0A0A]'
            }`}
          >
            {animated
              ? FLOW_TEXT.split('').map((char, index) => (
                  <span key={`${char}-${index}`} className={index < filledCount ? 'text-[#0A0A0A]' : undefined}>
                    {char}
                  </span>
                ))
              : FLOW_TEXT}
          </p>
        </div>
      </div>
    </section>
  );
};

const NewLandingPage = () => {
  const navigate = useNavigate();
  const { activeSectionId, scrollToSection } = useLandingSectionSpy();
  const goToSignup = () => navigate(PAGE.SIGNUP.path);
  const goToWelcome = () => navigate(PAGE.WELCOME.path);
  const [problemFrame, setProblemFrame] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [heroPhase, setHeroPhase] = useState<'mockup' | 'sides' | 'copy'>('mockup');
  const playSectionMotion = useMinWidth(XL_MIN_WIDTH);

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const updateHeight = () => setHeaderHeight(header.offsetHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setProblemFrame((current) => (current + 1) % PROBLEM_IMAGES.length);
    }, 1500);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <div className={`font-geist ${styles.landing}`}>
      <header ref={headerRef} className="sticky top-0 z-50 w-full p-4 lg:p-0 lg:bg-transparent">
        <LandingMobileMenu
          activeSectionId={activeSectionId}
          onNavigate={scrollToSection}
          barClassName={`w-full max-w-[1480px] mx-auto flex items-center justify-between gap-6 rounded-[20px] py-3 px-3 transition-colors duration-300 lg:bg-transparent lg:py-8 lg:px-10 xl:px-12 ${
            LANDING_NAVBAR_ON_COLOR_SECTION_IDS.includes(activeSectionId) ? 'bg-[#FFFFFF26]' : 'bg-[#1212140A]'
          }`}
        >
          {(menuButton) => (
            <>
              <img src={Logo} alt="Logo" className="hidden lg:block w-[120px] xl:w-[145px]" />
              <img src={LogoIcon} alt="Logo" className="lg:hidden" />

              <LandingNavbar
                activeSectionId={activeSectionId}
                onNavigate={scrollToSection}
                className="hidden lg:block"
              />

              <div className="hidden lg:flex lg:items-center lg:gap-6">
                <LandingLanguageSelector />
                <LandingButton className="text-sm xl:text-base" onClick={goToWelcome}>
                  Try for Free
                </LandingButton>
              </div>
              {menuButton}
            </>
          )}
        </LandingMobileMenu>
      </header>

      <MotionConfig reducedMotion="user">
        <section
          id="home"
          className={`relative flex flex-col bg-[#D7D1F7] scroll-mt-24 ${styles.hero}`}
          style={{ marginTop: -headerHeight, paddingTop: headerHeight }}
        >
          <img
            src={HeroGradientDecor}
            alt=""
            className="absolute inset-0 z-0 h-full w-full object-cover object-bottom lg:hidden"
          />
          <img
            src={HeroGradientDecor}
            alt="Hero Gradient Decor"
            className="absolute bottom-0 left-0 z-10 h-[53%] w-full"
          />

          <main
            className={`relative flex-1 min-h-0 flex items-end pb-[60px] [@media(max-width:767px)_and_(max-height:699px)]:pb-[40px] md:pb-[46px] 3xl:pb-[62px] ${styles.heroMain}`}
          >
            <div
              className={`relative w-[92%] md:w-full md:max-w-[700px] lg:w-[640px] lg:max-w-none xl:w-[800px] 2xl:w-[870px] 3xl:w-[917px] h-[67%] 2xl:h-[67%] 3xl:h-[70%] mb-14 [@media(max-width:767px)_and_(max-height:699px)]:mb-[64px] 2xl:mb-20 3xl:mb-14 mx-auto border border-white rounded-[32px] md:rounded-[100px] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#FFFFFF_100%)] shadow-[inset_0px_4px_50px_0px_#FFFFFF40] ${styles.heroFrame}`}
            >
              {playSectionMotion ? (
                <>
                  <motion.img
                    src={HeroImage1}
                    alt="Hero Image 1"
                    className="absolute -top-[130px] -left-[125px]"
                    initial={{ opacity: 0, y: 48, x: 36 }}
                    animate={heroPhase === 'mockup' ? { opacity: 0, y: 48, x: 36 } : { opacity: 1, y: 0, x: 0 }}
                    transition={{ duration: 0.7, ease: HERO_EASE }}
                  />
                  <motion.img
                    src={HeroImage4}
                    alt="Hero Image 4"
                    className="absolute top-[20px] -right-[70px]"
                    initial={{ opacity: 0, y: 48, x: -28 }}
                    animate={heroPhase === 'mockup' ? { opacity: 0, y: 48, x: -28 } : { opacity: 1, y: 0, x: 0 }}
                    transition={{ duration: 0.7, ease: HERO_EASE, delay: heroPhase === 'mockup' ? 0 : 0.08 }}
                  />
                  <motion.img
                    src={HeroMockup}
                    alt="Hero mockup"
                    className="absolute -top-[90px] left-1/2"
                    initial={{ x: '-50%', y: 56, opacity: 0 }}
                    animate={{ x: '-50%', y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, ease: HERO_EASE }}
                    onAnimationComplete={() => setHeroPhase((phase) => (phase === 'mockup' ? 'sides' : phase))}
                  />
                  <motion.img
                    src={HeroImage2}
                    alt="Hero Image 2"
                    className="absolute w-[272px] 3xl:w-[332px] top-[90px] 2xl:top-[110px] 3xl:top-[120px] 2xl:-left-[30px] 3xl:left-[40px]"
                    initial={{ opacity: 0, y: 48, x: 36 }}
                    animate={heroPhase === 'mockup' ? { opacity: 0, y: 48, x: 36 } : { opacity: 1, y: 0, x: 0 }}
                    transition={{ duration: 0.7, ease: HERO_EASE, delay: heroPhase === 'mockup' ? 0 : 0.12 }}
                  />
                  <motion.img
                    src={HeroImage3}
                    alt="Hero Image 3"
                    className="absolute top-[95px] right-[40px]"
                    initial={{ opacity: 0, y: 48, x: -28 }}
                    animate={heroPhase === 'mockup' ? { opacity: 0, y: 48, x: -28 } : { opacity: 1, y: 0, x: 0 }}
                    transition={{ duration: 0.7, ease: HERO_EASE, delay: heroPhase === 'mockup' ? 0 : 0.16 }}
                    onAnimationComplete={() => setHeroPhase((phase) => (phase === 'sides' ? 'copy' : phase))}
                  />
                </>
              ) : (
                <motion.img
                  src={FooterMockupImage}
                  alt=""
                  className={`absolute left-1/2 -top-[10%] w-full max-w-none -translate-x-1/2 [@media(max-width:767px)_and_(max-height:699px)]:-top-[40%] [@media(max-width:767px)_and_(max-height:800px)]:-top-[25%] md:-top-[34%] lg:w-[144%] ${styles.heroMockup}`}
                  initial={{ x: '-50%', y: 28, opacity: 0 }}
                  animate={{ x: '-50%', y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.5, ease: HERO_EASE }}
                />
              )}
            </div>
            <motion.div
              className={`px-4 md:px-0 absolute left-1/2 w-full max-w-[1210px] mx-auto z-20 rounded-[24px] ${styles.heroCopy}`}
              initial={playSectionMotion ? { x: '-50%', opacity: 0, y: 36 } : false}
              animate={
                !playSectionMotion || heroPhase === 'copy'
                  ? { x: '-50%', opacity: 1, y: 0 }
                  : { x: '-50%', opacity: 0, y: 36 }
              }
              transition={{ duration: playSectionMotion ? 0.6 : 0, ease: HERO_EASE }}
            >
              <h1 className="hidden md:block text-center uppercase font-roboto-condensed font-black text-[48px] md:text-[70px] 2xl:text-[76px] 3xl:text-[96px] text-[#0A0A0A] tracking-[-5.2px] 3xl:tracking-[-6.4px] leading-[82px] 2xl:leading-[88px] 3xl:leading-[115px]">
                <span>Track Simultaneously Every </span> <br />
                <span className="text-[#5F46DE]">Euro, Hour, dimension</span>
              </h1>
              <h1 className="md:hidden text-center uppercase font-roboto-condensed font-black text-[48px] [@media(max-width:767px)_and_(max-height:699px)]:text-[44px] md:text-[70px] 2xl:text-[76px] 3xl:text-[96px] text-[#0A0A0A] tracking-[-2.4px] leading-[43px]">
                <span>Track Simultaneously Every </span>
                <span className="text-[#5F46DE]">Euro, Hour, dimension</span>
              </h1>
              <p className="w-full mt-4 max-w-[358px] md:max-w-[686px] mx-auto text-base md:text-[18px] 3xl:text-[20px] text-center font-medium text-[#0A0A0A] tracking-[-0.32px] md:tracking-[-0.4px] leading-[24px] md:leading-[30px]">
                BatJet allocates every invoice and every work hour across departments, projects, contracts, and products
                all at once. Real numbers. Real time.
              </p>

              <div className="flex flex-col md:flex-row items-center gap-4 justify-center mt-[56px] [@media(max-width:767px)_and_(max-height:699px)]:mt-[36px] md:mt-8 2xl:mt-[40px] 3xl:mt-[52px]">
                <LandingButton className="w-full md:w-auto" onClick={goToWelcome}>
                  Try for Free
                </LandingButton>
                <LandingButton
                  variant="secondary"
                  className="w-full md:w-auto"
                  onClick={() => scrollToSection('how-it-works')}
                >
                  See How it Works
                </LandingButton>
              </div>
            </motion.div>
          </main>
        </section>
      </MotionConfig>

      <section
        id="problems"
        className="scroll-mt-24 px-5 py-16 md:px-[64px] md:pb-[60px] md:pt-[120px]"
        aria-label="Problems"
      >
        <LandingReveal
          enabled={playSectionMotion}
          className="mx-auto w-full max-w-[1312px] space-y-10 md:space-y-[56px]"
        >
          <div className="space-y-3">
            <div className="space-y-[20px]">
              <div className="py-1.5 px-4 rounded-full h-[34px] bg-background w-fit mx-auto text-[#383838CC] text-sm leading-[22px] tracking-[-0.42px] shadow-[0px_4px_12px_0px_#0000001A]">
                Problem
              </div>
              <h2 className="text-center text-[32px] font-medium leading-[40px] tracking-[-0.96px] text-[#0A0A0A] md:text-[48px] md:leading-[58px] md:tracking-[-2.88px]">
                Managing a budget shouldn't feel like managing chaos.
              </h2>
            </div>
            <p className="text-[#383838] text-center text-base leading-[24px] tracking-[-0.32px]">
              When every project, contract, and department tracks spend its own way, allocating money across your
              organization turns into guesswork dressed up as a process.
            </p>
          </div>

          <div className="flex flex-col gap-10 md:flex-row md:items-center md:gap-[120px]">
            <div className="relative w-full md:w-auto">
              {PROBLEM_IMAGES.map((src, index) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  className={`w-full transition-opacity duration-[1500ms] ${
                    index === 0 ? 'relative' : 'absolute inset-0'
                  } ${index === problemFrame ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}
            </div>

            <div className="w-full space-y-6 md:w-[55%] md:space-y-[48px] md:p-4">
              {PROBLEM_LIST_ITEMS.map((item) => (
                <div key={item} className="flex items-start gap-4 md:items-center">
                  <div className="size-6 shrink-0 rounded-[8px] bg-[#EF5DA8] text-white flex items-center justify-center">
                    <img src={CrossIcon} alt="" />
                  </div>
                  <p className="text-[#0A0A0A] text-base leading-[24px] tracking-[-0.32px] font-medium md:text-[20px] md:leading-[30px] md:tracking-[-0.4px]">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </LandingReveal>
      </section>

      <FlowSection animated={playSectionMotion} />

      <section id="solutions" className="scroll-mt-24 px-5 py-16 md:px-[64px] md:py-[60px]" aria-label="Solutions">
        <LandingReveal
          enabled={playSectionMotion}
          className="mx-auto w-full max-w-[1312px] space-y-10 md:space-y-[56px]"
        >
          <div className="space-y-3">
            <div className="space-y-[20px]">
              <div className="py-1.5 px-4 rounded-full h-[34px] bg-background w-fit mx-auto text-[#383838CC] text-sm leading-[22px] tracking-[-0.42px] shadow-[0px_4px_12px_0px_#0000001A]">
                Solution
              </div>
              <h2 className="text-center text-[32px] font-medium leading-[40px] tracking-[-0.96px] text-[#0A0A0A] md:text-[48px] md:leading-[58px] md:tracking-[-2.88px]">
                Say goodbye to budget guesswork.
              </h2>
            </div>
            <p className="text-[#383838] text-center text-base leading-[24px] tracking-[-0.32px]">
              With BatJet, every project, every invoice whether an expense or a revenue, and every hour your team works
              flow into the same structure. <br className="hidden md:block" /> So what you planned and what's actually
              happening are always the same number.
            </p>
          </div>

          <img src={SolutionImage} alt="" className="mx-auto w-full max-w-[420px] md:max-w-[800px]" />

          <div className="mx-auto grid max-w-[930px] grid-cols-1 gap-6 md:grid-cols-2 md:gap-10">
            {[
              'Every project, contract, product and service, department have their Accounting Center',
              'Plan vs. actual visible in real time including the insight',
              "Invoices allocated the moment they're uploaded",
              'Every work hour converted to real cost and allocated'
            ].map((item) => (
              <div key={item} className="flex items-start gap-4 md:items-center">
                <div className="min-w-6 min-h-6 shrink-0 rounded-[8px] bg-[#5F46DE] text-white flex items-center justify-center">
                  <img src={CheckIcon} alt="" />
                </div>
                <p className="text-[#0A0A0A] text-base font-medium leading-[24px] tracking-[-0.32px] md:text-[20px] md:leading-[30px] md:tracking-[-0.4px]">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </LandingReveal>
      </section>

      <section
        id="features"
        className="scroll-mt-24 px-5 py-16 md:px-[64px] md:pb-[120px] md:pt-[110px]"
        aria-label="Features"
      >
        <LandingReveal
          enabled={playSectionMotion}
          className="mx-auto w-full max-w-[1312px] space-y-10 md:space-y-[56px]"
        >
          <div className="space-y-3">
            <div className="space-y-[20px]">
              <div className="py-1.5 px-4 rounded-full h-[34px] bg-background w-fit mx-auto text-[#383838CC] text-sm leading-[22px] tracking-[-0.42px] shadow-[0px_4px_12px_0px_#0000001A]">
                Features
              </div>
              <h2 className="text-center text-[32px] font-medium leading-[40px] tracking-[-0.96px] text-[#0A0A0A] md:text-[48px] md:leading-[58px] md:tracking-[-2.88px]">
                Everything your budget needs built into one structure.
              </h2>
            </div>
            <p className="text-[#383838] text-center text-base leading-[24px] tracking-[-0.32px]">
              From the moment you set your budget to the moment an invoice or a logged hour gets approved, BatJet keeps
              every Accounting Center connected.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
            {FEATURE_CARDS.map((card) => (
              <div
                key={card.title}
                className={`col-span-1 ${card.colSpan} rounded-[20px] border border-[#0013051A] bg-[linear-gradient(180deg,#FFFFFF_0%,#F4F4F4_69.71%,#FAFAFA_100%)] p-5 pb-0 md:p-[32px]`}
              >
                <p className="text-left text-[22px] font-medium leading-[30px] tracking-[-0.66px] text-[#0A0A0A] md:text-center md:text-[24px] md:leading-[34px] md:tracking-[-0.72px]">
                  {card.title}
                </p>
                <p className="mt-2 text-left text-base leading-[24px] tracking-[-0.32px] text-[#383838] md:text-center">
                  {card.description}
                </p>
                <img src={card.image} alt="" className="mx-auto mt-6 w-full" />
              </div>
            ))}
          </div>
        </LandingReveal>
      </section>

      <HowItWorksSection />

      <section
        id="ecosystem"
        className="scroll-mt-24 px-5 py-16 md:px-[64px] md:pb-[60px] md:pt-[120px]"
        aria-label="Ecosystem"
      >
        <LandingReveal
          enabled={playSectionMotion}
          className="mx-auto w-full max-w-[1312px] space-y-10 md:space-y-[56px]"
        >
          <div className="space-y-3">
            <div className="space-y-[20px]">
              <div className="py-1.5 px-4 rounded-full h-[34px] bg-background w-fit mx-auto text-[#383838CC] text-sm leading-[22px] tracking-[-0.42px] shadow-[0px_4px_12px_0px_#0000001A]">
                Ecosystem
              </div>
              <h2 className="text-center text-[32px] font-medium leading-[40px] tracking-[-0.96px] text-[#0A0A0A] md:text-[48px] md:leading-[58px] md:tracking-[-2.88px]">
                Built for the whole Organization
              </h2>
            </div>
            <p className="text-[#383838] max-w-[720px] mx-auto text-center text-base leading-[24px] tracking-[-0.32px]">
              Finance shouldn't happen in a vacuum. BatJet connects every tier of your company through tailored
              interfaces and permissions, giving everyone the exact tools they need to stay productive and aligned.
            </p>
          </div>

          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
            <img className="h-auto w-full" src={EcosystemBudgetOwnerImage} alt="" />
            <img className="h-auto w-full" src={EcosystemBudgetValidatorImage} alt="" />
            <img className="h-auto w-full" src={EcosystemBudgetMemberImage} alt="" />
          </div>
        </LandingReveal>
      </section>

      <section
        id="insight"
        className="scroll-mt-24 px-5 py-16 md:px-[64px] md:pb-[60px] md:pt-[120px]"
        aria-label="Insight"
      >
        <LandingReveal
          enabled={playSectionMotion}
          className="mx-auto w-full max-w-[1312px] space-y-10 md:space-y-[56px]"
        >
          <div className="space-y-3">
            <div className="space-y-[20px]">
              <div className="py-1.5 px-4 rounded-full h-[34px] bg-background w-fit mx-auto text-[#383838CC] text-sm leading-[22px] tracking-[-0.42px] shadow-[0px_4px_12px_0px_#0000001A]">
                Insight
              </div>
              <h2 className="text-center text-[32px] font-medium leading-[40px] tracking-[-0.96px] text-[#0A0A0A] md:text-[48px] md:leading-[58px] md:tracking-[-2.88px]">
                Understand the thinking behind BatJet
              </h2>
            </div>
            <p className="text-[#383838] text-center text-base leading-[24px] tracking-[-0.32px]">
              Explore tactical guides, industry deep dives, and expert perspectives on scaling budget infrastructure,
              eliminating manual tracking silos, and optimizing organizational spend.
            </p>
          </div>

          <InsightCarousel />
        </LandingReveal>
      </section>

      <section id="faq" className="scroll-mt-24 px-5 py-16 md:px-[64px] md:pb-[60px] md:pt-[120px]" aria-label="FAQ">
        <LandingReveal
          enabled={playSectionMotion}
          className="mx-auto w-full max-w-[1312px] space-y-10 md:space-y-[56px]"
        >
          <div className="space-y-3">
            <div className="space-y-[20px]">
              <div className="py-1.5 px-4 rounded-full h-[34px] bg-background w-fit mx-auto text-[#383838CC] text-sm leading-[22px] tracking-[-0.42px] shadow-[0px_4px_12px_0px_#0000001A]">
                FAQ
              </div>
              <h2 className="text-center text-[32px] font-medium leading-[40px] tracking-[-0.96px] text-[#0A0A0A] md:text-[48px] md:leading-[58px] md:tracking-[-2.88px]">
                Everything you need to know.
              </h2>
            </div>
            <p className="text-[#383838] text-center text-base leading-[24px] tracking-[-0.32px]">
              Got questions about early access, data governance, or how BatJet fits into your current workflow? Here are
              the answers to what organizations ask us most
            </p>
          </div>

          <Accordion type="single" collapsible className="max-w-[620px] mx-auto space-y-4">
            {FAQ_ITEMS.map((item, index) => (
              <AccordionItem
                key={item.question}
                value={`faq-${index}`}
                className="overflow-hidden rounded-[12px] border border-[#0013051A] bg-white data-[state=open]:bg-[linear-gradient(180deg,#FFFFFF_0%,#F4F4F4_69.71%,#FAFAFA_100%)]"
              >
                <AccordionTrigger className="gap-4 px-4 py-4 hover:no-underline [&>span]:hidden md:gap-7 md:px-8 md:py-6">
                  <span className="flex w-full items-center justify-between gap-4 md:gap-6">
                    <span className="text-left text-base font-medium leading-[24px] tracking-[-0.32px] text-[#0A0A0A] md:text-[20px] md:leading-[30px] md:tracking-[-0.4px]">
                      {item.question}
                    </span>
                    <img src={PlusIcon} alt="" className="shrink-0 [[data-state=open]_&]:hidden" />
                    <img src={MinusIcon} alt="" className="hidden shrink-0 [[data-state=open]_&]:block" />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="border-t border-[#0013051A] px-4 py-4 text-base font-normal leading-[24px] tracking-[-0.32px] text-[#383838] md:px-8 md:py-6">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </LandingReveal>
      </section>

      <section id="ready" className="pt-[30px] px-4 scroll-mt-24" aria-label="ready">
        <LandingReveal
          enabled={playSectionMotion}
          className="w-full rounded-[36px] bg-[linear-gradient(180deg,#8774E6_0%,#D7D1F7_98.94%)] px-5 pt-16 md:px-0 md:pt-[120px]"
        >
          <div className="space-y-3">
            <div className="space-y-[20px]">
              <h2 className="text-center text-[32px] font-medium leading-[40px] tracking-[-0.96px] text-white md:text-[48px] md:leading-[58px] md:tracking-[-2.88px]">
                Ready to take control of your numbers?
              </h2>
            </div>
            <p className="mx-auto max-w-[540px] text-center text-base leading-[24px] tracking-[-0.32px] text-white md:text-[20px] md:leading-[30px] md:tracking-[-0.4px]">
              Batjet keeps your cashflow, invoices, and expenses clear, so you always know what’s next.
            </p>
          </div>

          <LandingButton className="mx-auto mt-8 block md:mt-[48px]" onClick={goToWelcome}>
            Start for Free
          </LandingButton>

          <img src={FooterMockupImage} alt="" className="mx-auto mt-10 w-full max-w-[1200px] md:mt-[60px]" />
        </LandingReveal>
      </section>

      <footer className="mx-auto flex h-auto max-w-[1200px] scroll-mt-24 flex-col items-center justify-between gap-8 px-5 pt-12 md:h-[442px] md:gap-4 md:px-10 md:pt-[72px]">
        <div className="flex flex-col items-center justify-center gap-4 text-center md:gap-6">
          <img src={Logo} alt="" className="h-10 w-auto md:h-[56px] md:w-[253px]" />
          <p className="text-base font-medium leading-[24px] tracking-[-0.32px] text-[#383838] md:text-[20px] md:leading-[30px] md:tracking-[-0.4px]">
            Budget planning, allocation, and invoice tracking. All in one place
          </p>
        </div>

        <nav>
          <ul className="flex flex-col items-center gap-2 md:flex-row md:gap-4">
            {ALL_LANDING_NAV_ITEMS.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => scrollToSection(item.id)}
                  className="px-4 py-2 text-[#0A0A0A] text-base tracking-[-0.32px] hover:underline"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center justify-center bg-[#121214] rounded-t-[16px] py-4 px-6 w-full max-w-[600px]">
          <p className="text-white text-xs md:text-sm leading-[22px] tracking-[-0.42px]">
            © 2026 Batjet. All Rights reserved. Powered by EagleApps
          </p>
        </div>
      </footer>
    </div>
  );
};

export default NewLandingPage;
